import * as userLogout from 'api/user/PostUserLogoutV1';
import ENTITLEMENTS from 'enums/entitlements';
import PERMISSIONS from 'enums/permissions';
import { runOnce } from 'functions/concurrency';
import delay from 'functions/delay';
import { RenewalError, getUserRefreshToken } from 'functions/getUserRefreshToken';
import { Optional } from 'functions/promiseExtensions';
import IToken from 'interfaces/IToken';
import ITokenJWT from 'interfaces/ITokenJWT';
import ITokens from 'interfaces/ITokens';

const TOKEN_LOCAL_STORAGE_KEY = 'tokens';

function isTimeStampValid(time: number): boolean {
	const currentTime: number = Math.floor(new Date().getTime() / 1000);
	if (currentTime < time) {
		return true;
	}
	return false;
}

function parseJwt(token: string): ITokenJWT {
	var base64Url = token.split('.')[1];
	var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	var jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join('')
	);
	return JSON.parse(jsonPayload);
}

export function setTokensToLocalStorage(tokens: ITokens): void {
	localStorage.setItem(TOKEN_LOCAL_STORAGE_KEY, JSON.stringify(tokens));
}

export function getTokensFromLocalStorage(): Optional<ITokens> {
	return Optional.Maybe(localStorage.getItem(TOKEN_LOCAL_STORAGE_KEY)).map(JSON.parse);
}

function removeTokensFromLocalStorage(): void {
	localStorage.removeItem(TOKEN_LOCAL_STORAGE_KEY);
}

export function getValidAccessTokenFromLocalStorage(): Optional<IToken> {
	return getTokensFromLocalStorage()
		.keep((token: ITokens) => token.accessToken)
		.filter((x) => isTimeStampValid(x.expiresAtUnixTimestamp));
}

function getValidRefreshTokenFromLocalStorage(): Optional<IToken> {
	return getTokensFromLocalStorage()
		.keep((token) => token.refreshToken)
		.filter((x) => isTimeStampValid(x.expiresAtUnixTimestamp));
}

function getPotentiallyExpiredJwtFromAccessToken(): Optional<ITokenJWT> {
	return getTokensFromLocalStorage()
		.keep((token: ITokens) => token.accessToken)
		.map((x: IToken) => x.token)
		.map(parseJwt);
}

async function getRefreshTokenAndStoreLocally(refreshToken: IToken): Promise<Optional<IToken>> {
	try {
		var tokens = await getUserRefreshToken(refreshToken);
		return tokens
			.do(setTokensToLocalStorage)
			.orElseDo(removeTokensFromLocalStorage)
			.keep((x) => x.accessToken);
	} catch (error) {
		if (error === RenewalError.NetworkConnectivityProblem) {
			console.error('RenewalError.NetworkConnectivityProblem');
			// since the refreshtoken is valid, but we don't have a response from the server.
			// also the access token is invalid, so there's nothing we can return to the caller.
			return Optional.None<IToken>();
		}
		if (error === RenewalError.RenewalJWTInvalid) {
			console.error('RenewalError.RenewalJWTInvalid');
			restart();
			// there will be clients waiting on the renewal to continue, however, they cannot.
			// this solution reloads the page and delays the client's await for hopefully so long that the page
			// will be reloaded and the waiting clients will be removed by the browser before it comes to an error.
			await delay(2000);
			return Optional.None<IToken>();
		}
		console.error(error);

		return Optional.None<IToken>();
	}
}

export async function refreshToken(): Promise<Optional<IToken>> {
	var actualRefreshToken = getValidRefreshTokenFromLocalStorage();

	// Multiple callers might cause the refresh fn to be executed more then once.
	return await actualRefreshToken.mapAsync((token) =>
		runOnce(token.token, () => getRefreshTokenAndStoreLocally(token))
	);
}
export async function getAccessTokenPotentiallyRenewingIt(): Promise<Optional<IToken>> {
	return await getValidAccessTokenFromLocalStorage().orElseAsyncOptional(refreshToken);
}

export function restart(): void {
	localStorage.clear();
	window.location.href = window.location.origin + '/login';
}

export async function logout(): Promise<void> {
	try {
		const accessToken = await getAccessTokenPotentiallyRenewingIt();
		const refreshToken = getValidRefreshTokenFromLocalStorage();

		return accessToken
			.zip(refreshToken)
			.doAsync(([a, r]) => userLogout.callApi(a, r))
			.then(restart);
	} catch (error) {
		// Good question: User wants to logout, but there's no network connectivity to invalidate the renewal JWT
		// Recommended behavior: We still log out and ignore the error, which leaves a zombie jwt in the backend.
		// Reasoning: It seems awkward to tell the user that he cannot logout because the backend is not available
		restart();
	}
}

export function isUserAdmin(): boolean {
	return getPotentiallyExpiredJwtFromAccessToken()
		.flatMap((x: ITokenJWT) => x.permission ?? [])
		.includes(PERMISSIONS.ADMINISTRATE);
}

export function getTenantNodeIds(): string[] {
	const jwt = getPotentiallyExpiredJwtFromAccessToken();
	if (!jwt.hasValue()) return [];

	const jwtData = jwt.get();
	const nodeIdsJson = jwtData['Tenant.NodeIds'] as string | undefined;
	if (!nodeIdsJson) return [];

	try {
		// Parse the JSON string like '["3fa85f64-5717-4562-b3fc-2c963f66afa6"]'
		const parsed = JSON.parse(nodeIdsJson) as string[];
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		console.warn('Failed to parse Tenant.NodeIds from JWT:', error);
		return [];
	}
}

export function getUserPermissions(): Optional<PERMISSIONS[]> {
	return getPotentiallyExpiredJwtFromAccessToken().map((x: ITokenJWT) => x.permission ?? []);
}

export function getUserEntitlements(): Optional<ENTITLEMENTS[]> {
	return getPotentiallyExpiredJwtFromAccessToken().map((x: ITokenJWT) => x.entitlement ?? []);
}

export function isUserWriter(): boolean {
	return getPotentiallyExpiredJwtFromAccessToken()
		.flatMap((x) => x.permission ?? [])
		.includes(PERMISSIONS.WRITE);
}

export function getUserEmail(): string {
	return getPotentiallyExpiredJwtFromAccessToken()
		.map((x) => x.email)
		.getOrDefault('');
}

export function getTenantId(): string {
	return getPotentiallyExpiredJwtFromAccessToken()
		.map((x) => x['Tenant.Id'] ?? '')
		.getOrDefault('');
}

export function arePermissionsInUserPermissions(permissions: PERMISSIONS[]): boolean {
	if (!permissions.length) return true;

	const userPermissions: PERMISSIONS[] = getUserPermissions().getOrDefault([]);

	return permissions.some((i) => userPermissions.includes(i));
}

export function areEntitlementsInUserEntitlements(entitlements: ENTITLEMENTS[]): boolean {
	if (!entitlements.length) return true;

	const userEntitlements: ENTITLEMENTS[] = getUserEntitlements().getOrDefault([]);

	return entitlements.some((i) => userEntitlements.includes(i));
}
